import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { EncryptionService } from './encryption.service';
import { requestToken } from '../../utils/auth.utils';

const ACCOUNT_STATUS_API =
  'https://kipiv6n4oi.execute-api.us-west-1.amazonaws.com/default/fetch-user-account-status';

export interface StorageStatus {
  used_gb: number;
  total_gb: number;
  pct_used: number;
  alert_level: 'warn_75' | 'warn_90' | 'warn_95' | 'full' | null;
}

export interface PlanStatus {
  plan_name: string;
  plan_type: string;
  expiry_date: string;
  grace_ends_at: string | null;
  days_until_expiry: number | null;
  days_in_grace: number | null;
  alert_level: 'warn_30' | 'warn_14' | 'warn_7' | 'warn_1' | 'grace' | 'expired' | null;
}

export interface StatusBanner {
  trigger_key: string;
  message: string;
  sub_message: string;
  cta_text: string;
  cta_link: string;
  color: string;
  is_dismissible: number;
}

export interface AccountStatus {
  success: boolean;
  storage: StorageStatus;
  plan: PlanStatus;
  banners: StatusBanner[];
}

@Injectable({ providedIn: 'root' })
export class AccountStatusService {
  private _status = new BehaviorSubject<AccountStatus | null>(null);
  private _loading = new BehaviorSubject<boolean>(false);
  private refToken: any;

  readonly status$ = this._status.asObservable();
  readonly loading$ = this._loading.asObservable();

  get snapshot(): AccountStatus | null {
    return this._status.getValue();
  }

  constructor(
    private http: HttpClient,
    private afAuth: AngularFireAuth,
    private encryptionService: EncryptionService
  ) {}

  async fetchStatus(): Promise<void> {
    this._loading.next(true);
    this._status.next(null); // clear stale cache so old banners don't flash on navigation
    try {
      const headers = await this.buildHeaders();
      if (!headers) {
        this._loading.next(false);
        return;
      }
      this.http.get<AccountStatus>(ACCOUNT_STATUS_API, { headers }).subscribe({
        next: (data) => {
          this._status.next(data);
          this._loading.next(false);
        },
        error: () => {
          this._loading.next(false);
        },
      });
    } catch {
      this._loading.next(false);
    }
  }

  private async buildHeaders(): Promise<HttpHeaders | null> {
    const encryptedData = localStorage.getItem('user');
    if (!encryptedData) return null;
    const user = JSON.parse(this.encryptionService.decryptData(encryptedData) || '{}');
    const token: string = user?.stsTokenManager?.accessToken;
    const expirationTime = user?.stsTokenManager?.expirationTime;
    const userCurrentTime = new Date();
    const userExpirationTime = new Date(expirationTime);

    let finalToken = token;
    if (userCurrentTime >= userExpirationTime) {
      const refreshed = await requestToken(this.afAuth);
      if (refreshed) this.refToken = refreshed;
      finalToken = this.refToken;
    }

    if (!finalToken) return null;
    return new HttpHeaders().set('Authorization', finalToken);
  }
}
